import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import api from '~services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DeviceStateMap = Record<string, any>;
export type DisabledEventsMap = Record<string, boolean>;

type DeviceStateContextValue = {
  deviceState: DeviceStateMap;
  disabledEvents: DisabledEventsMap;
  updateDeviceState: (item: { id: string; state: any }) => void;
  setDisabledEvents: (id: string, disabled: boolean) => void;
  resetDeviceState: () => void;
};

const DeviceStateContext = createContext<DeviceStateContextValue | undefined>(
  undefined
);

// ---------------------------------------------------------------------------
// Imperative API — for use outside React (e.g. SignalR module)
// ---------------------------------------------------------------------------

let _imperativeApi: {
  updateDeviceState: (item: { id: string; state: any }) => void;
  setDisabledEvents: (id: string, disabled: boolean) => void;
  resetDeviceState: () => void;
} | null = null;

export function getDeviceStateApi() {
  return _imperativeApi;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function DeviceStateProvider({ children }: { children: ReactNode }) {
  const [deviceState, setDeviceState] = useState<DeviceStateMap>({});
  const [disabledEvents, setDisabledEventsMap] = useState<DisabledEventsMap>(
    {}
  );

  // Keep a ref to disabledEvents so the updateDeviceState callback can read
  // the latest value without re-creating.
  const disabledRef = useRef(disabledEvents);
  disabledRef.current = disabledEvents;

  const updateDeviceState = useCallback((item: { id: string; state: any }) => {
    if (disabledRef.current[item.id]) return;

    setDeviceState(prev => {
      const existing = prev[item.id] ?? {};
      const newState = { ...existing };

      // RSSI history accumulation — mirrors the old commonSlice reducer.
      if (item.state?.rssi) {
        newState.rssiHistory = newState.rssiHistory
          ? [
              ...newState.rssiHistory,
              {
                timestamp: new Date().getTime(),
                rssi: item.state.rssi,
              },
            ]
          : [
              {
                timestamp: new Date().getTime(),
                rssi: item.state.rssi,
              },
            ];
      }

      return {
        ...prev,
        [item.id]: {
          ...newState,
          ...item.state,
        },
      };
    });
  }, []);

  const setDisabledEvents = useCallback((id: string, disabled: boolean) => {
    setDisabledEventsMap(prev => ({ ...prev, [id]: disabled }));
  }, []);

  const resetDeviceState = useCallback(() => {
    setDeviceState({});
    setDisabledEventsMap({});
  }, []);

  // Expose the functions to imperative (non-React) callers.
  useEffect(() => {
    _imperativeApi = { updateDeviceState, setDisabledEvents, resetDeviceState };
    return () => {
      _imperativeApi = null;
    };
  }, [updateDeviceState, setDisabledEvents, resetDeviceState]);

  const value = useMemo(
    () => ({
      deviceState,
      disabledEvents,
      updateDeviceState,
      setDisabledEvents,
      resetDeviceState,
    }),
    [
      deviceState,
      disabledEvents,
      updateDeviceState,
      setDisabledEvents,
      resetDeviceState,
    ]
  );

  return (
    <DeviceStateContext.Provider value={value}>
      {children}
    </DeviceStateContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useDeviceStateContext(): DeviceStateContextValue {
  const ctx = useContext(DeviceStateContext);
  if (!ctx)
    throw new Error(
      'useDeviceStateContext must be used within DeviceStateProvider'
    );
  return ctx;
}

/** Convenience: returns the live state for a single device. */
export function useDeviceState(deviceId: string | undefined): any | undefined {
  const { deviceState } = useDeviceStateContext();
  return deviceId ? deviceState[deviceId] : undefined;
}

/** Returns the full deviceState map. */
export function useAllDeviceState(): DeviceStateMap {
  const { deviceState } = useDeviceStateContext();
  return deviceState;
}

// ---------------------------------------------------------------------------
// Plain async function — replaces the fetchDeviceLastReportedState thunk
// ---------------------------------------------------------------------------

export async function fetchDeviceLastReportedState(
  deviceRegisterId: string,
  deviceId: string
) {
  const res = await api.http.get(
    `/Providers/Cozify.DeviceRegister/${deviceRegisterId}/Devices/${deviceId}/state`
  );
  getDeviceStateApi()?.updateDeviceState({ id: deviceId, state: res.data });
  return { id: deviceId, state: res.data };
}

// ---------------------------------------------------------------------------
// Plain async function — replaces the sendDeviceCommand thunk
// ---------------------------------------------------------------------------

export async function sendDeviceCommand(args: {
  deviceRegisterId: string;
  deviceId: string;
  command: any;
}) {
  await api.http.post(
    `/Providers/Cozify.DeviceRegister/${args.deviceRegisterId}/Devices/${args.deviceId}/commands`,
    {
      payload: args.command,
    }
  );
}
